<script setup lang="ts">

    import { ref, Ref, watch } from 'vue';
    import Spinner from '@/shared/components/Spinner.vue';
    import { sqlQuery } from '@/shared/helpers/api.ts';
    import { http } from '@/shared/httpWrapper';
    import BoolElement from '@/shared/components/BoolElement.vue';

    const selectedAssociationScope = defineModel<string>({ required: true });

    const props = defineProps({
        selectedProject: {
            type: String,
            required: true
        }
    });

    type Project = {
        Scope: string;
        ScopeDescription: string;
        IsValid: boolean;
        DeliveryCode: string;
        DeliveryCodeDescription: string;
        Description: string;
    };

    const loading: Ref<boolean> = ref(true);
    const selectedProjectAssociationRow: Ref<Project | null> = ref(null);

    watch(selectedProjectAssociationRow, (newVal) => {
        if (newVal) {
            selectedAssociationScope.value = newVal.Scope;
        }
    });

    const projects: Ref<Project[]> = ref([]);

    watch(() => props.selectedProject, async (selectedProject) => {
        if (selectedProject) {
            loading.value = true;
            projects.value = (await sqlQuery(http, {
                query: `SELECT
                          Scope,
                          sc.Description AS ScopeDescription,
                          p.IsValid,
                          p.DeliveryCode,
                          dc.Description as DeliveryCodeDescription,
                          p.Description as Description
                        FROM LCIProject p
                        JOIN Facility sc ON p.Scope = sc.Name AND p.Name = @project
                        LEFT JOIN LCIDeliveryCode dc ON p.DeliveryCode_ID = dc.Id
                        ORDER BY Scope`,
                arguments: { project: selectedProject }
            })).data as Project[];
            selectedProjectAssociationRow.value = projects.value.find(p => p.Scope === selectedAssociationScope.value)!; // If this fails, something has gone terribly wrong
            loading.value = false;
        }
    }, { immediate: true });

    function deliveryCodeDisplay(deliveryCode: string, deliveryCodeDescription: string): string {
        if (deliveryCode) {
            return `${deliveryCode} - ${deliveryCodeDescription}`;
        } else {
            return '';
        }
    }

</script>

<template>
    <div class="panel is-success">
        <div class="panel-heading">
            Project associations
        </div>
        <spinner
            :loading="loading"
            :fullscreen="false">
            <b-table
                v-model:selected="selectedProjectAssociationRow"
                sticky-header
                height="max-content"
                :data="projects">
                <b-table-column
                    v-slot="slotProps"
                    label="Facility"
                    field="Facility">
                    {{ slotProps.row.Scope }}
                </b-table-column>
                <b-table-column
                    v-slot="slotProps"
                    label="Facility Description"
                    field="FacilityDescription">
                    {{ slotProps.row.ScopeDescription }}
                </b-table-column>
                <b-table-column
                    v-slot="slotProps"
                    label="IsValid"
                    field="IsValid">
                    <bool-element :value="slotProps.row.IsValid" />
                </b-table-column>
                <b-table-column
                    v-slot="slotProps"
                    label="DeliveryCode"
                    field="DeliveryCode">
                    {{ deliveryCodeDisplay(slotProps.row.DeliveryCode, slotProps.row.DeliveryCodeDescription) }}
                </b-table-column>
                <b-table-column
                    v-slot="slotProps"
                    label="Description"
                    field="Description">
                    {{ slotProps.row.Description }}
                </b-table-column>
            </b-table>
        </spinner>
    </div>
</template>

<style scoped>
    .panel :deep(.table-wrapper) {
        height: fit-content;
        max-height: 300px;
    }
</style>
